import { ReactNode, useEffect, useState } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { BgwProvider } from "./contexts/backend_gateway/provider";
import { JoyThemed } from "./frameworks/joy/joy_themed";
import { ProfilePage } from "./pages/profile";
import { WelcomePage } from "./pages/welcome";
import { OrganizationsPage } from "./pages/organizations";
import { EditOrganizationPane } from "./pages/organizations/edit_organization";
import { ServiceStatusesPage } from "./pages/service_statuses";
import { OnboarderContainer } from "./components/onboarder/container";
import { AlertsProvider } from "./contexts/alerts/provider";
import { Layout } from "./layouts/drawer_layout/layout";
import { DashboardPage } from "./pages/dashboard";
import { MyTeamPage } from "./pages/my_team";
// import { OrganizationSetupPage } from "./pages/organization_setup";
import { EditUserPane } from "./pages/my_team/edit_user";
import { SetOrganizationPage } from "./pages/set_organization";
import { MyEnvironmentPage } from "./pages/my_environment";
import { MyEnvironmentHelpPane } from "./pages/my_environment/help_pane";
import { FeaturesProvider } from "./contexts/features/provider";
import { MyAnyStackPage } from "./pages/my_any_stack";
import { EnvironmentsProvider } from "./contexts/environments/provider";
import { QuickLaunchPage } from "./pages/quick_launch";
import { QuickLaunchHelpPane } from "./pages/quick_launch/help_pane";
import { MyOldEnvironmentPage } from "./pages/my_old_environment";
import { MyOldEnvironmentHelpPane } from "./pages/my_old_environment/help_pane";
import { fetchAuthSession, signInWithRedirect } from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";

// OAuth Callback component - handles redirect after login
const CallbackComponent = () => {
  const [status, setStatus] = useState("Processing authentication...");

  useEffect(() => {
    const handleCallback = async () => {
      try {
        setStatus("Completing sign in...");

        // Wait for Amplify to process the OAuth callback
        // The URL should contain the authorization code
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get("code");
        const error = urlParams.get("error");

        if (error) {
          console.error("OAuth error:", error);
          setStatus("Authentication failed. Redirecting to login...");
          setTimeout(() => {
            window.location.href = "/";
          }, 2000);
          return;
        }

        if (!code) {
          console.error("No authorization code found");
          setStatus("Authentication failed. Redirecting to login...");
          setTimeout(() => {
            window.location.href = "/";
          }, 2000);
          return;
        }

        // Wait a bit for Amplify to process the callback
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // Check if we're now authenticated
        try {
          const session = await fetchAuthSession();
          if (session.tokens) {
            setStatus("Authentication successful! Redirecting...");
            // Clear the URL parameters and redirect
            window.history.replaceState({}, document.title, "/");
            window.location.href = "/";
          } else {
            throw new Error("No tokens found");
          }
        } catch (authError) {
          console.error("Auth check failed:", authError);
          setStatus("Authentication failed. Redirecting to login...");
          setTimeout(() => {
            window.location.href = "/";
          }, 2000);
        }
      } catch (error) {
        console.error("Callback error:", error);
        setStatus("Authentication error. Redirecting to login...");
        setTimeout(() => {
          window.location.href = "/";
        }, 2000);
      }
    };

    handleCallback();
  }, []);

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        flexDirection: "column",
        gap: "20px",
      }}
    >
      <h2>{status}</h2>
      <div>Please wait while we complete your authentication.</div>
    </div>
  );
};

// Logout component - handles redirect after logout
const LogoutComponent = () => {
  useEffect(() => {
    // Clean up any remaining session data and redirect to home
    // Amplify handles the logout cleanup automatically
    window.location.href = "/";
  }, []);

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        flexDirection: "column",
        gap: "20px",
      }}
    >
      <h2>Signing out...</h2>
      <div>Please wait while we complete your sign out.</div>
    </div>
  );
};

// Login component that automatically redirects to hosted UI
const LoginComponent = () => {
  useEffect(() => {
    // Automatically redirect to hosted login page
    const redirectToLogin = async () => {
      try {
        await signInWithRedirect();
      } catch (error) {
        console.error("Login redirect error:", error);
      }
    };

    redirectToLogin();
  }, []);

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        flexDirection: "column",
        gap: "20px",
      }}
    >
      <h2>Redirecting to sign in...</h2>
      <div>Please wait while we redirect you to the sign-in page.</div>
    </div>
  );
};

const AuthCheck = (props: { loading: ReactNode; authenticated: ReactNode }) => {
  const { loading, authenticated } = props;
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        setIsLoading(true);
        // fetchAuthSession automatically handles token refresh
        const session = await fetchAuthSession({ forceRefresh: false });
        setIsAuthenticated(!!session.tokens);
        setError(null);
      } catch (err) {
        console.error("Auth check error:", err);
        setError(err instanceof Error ? err.message : "Authentication failed");
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();

    // Listen for auth events including token refresh
    const unsubscribe = Hub.listen("auth", ({ payload }) => {
      switch (payload.event) {
        case "signedIn":
          setIsAuthenticated(true);
          setError(null);
          break;
        case "signedOut":
          setIsAuthenticated(false);
          break;
        case "signInWithRedirect_failure":
          setError("Sign in failed");
          setIsAuthenticated(false);
          break;
        case "tokenRefresh":
          // Token was refreshed successfully - no action needed
          console.log("Token refreshed successfully");
          break;
        case "tokenRefresh_failure":
          // Token refresh failed - user may need to re-authenticate
          console.error("Token refresh failed:", payload.data);
          setError("Session expired. Please sign in again.");
          setIsAuthenticated(false);
          break;
      }
    });

    return unsubscribe;
  }, []);

  if (isLoading) {
    return loading;
  } else if (error) {
    return <div>Error: {error}</div>;
  } else if (!isAuthenticated) {
    return <LoginComponent />;
  }
  return authenticated;
};

const App = () => {
  return (
    <BrowserRouter>
      <Routes>
        {/* OAuth callback and logout routes - no authentication required */}
        <Route path="/callback" element={<CallbackComponent />} />
        <Route path="/logout" element={<LogoutComponent />} />

        {/* All other routes require authentication */}
        <Route
          path="/*"
          element={
            <AuthCheck
              loading={<div>Loading...</div>}
              authenticated={
                <BgwProvider>
                  <AlertsProvider>
                    <FeaturesProvider>
                      <EnvironmentsProvider>
                        <JoyThemed>
                          <OnboarderContainer
                            onboardingComponent={<QuickLaunchPage />}
                            selectCompanyComponent={<SetOrganizationPage />}
                          >
                            <Routes>
                              {/* <Route
                                path="/onboarding/"
                                element={<OrganizationSetupPage />}
                              />
                              <Route
                                path="/onboarding/:deploymentId/:stepId"
                                element={<OrganizationSetupPage />}
                              /> */}
                              <Route
                                path="/quick_launch"
                                element={<QuickLaunchPage />}
                              />
                              <Route
                                path="/quick_launch/:serviceType"
                                element={<QuickLaunchPage />}
                              />
                              <Route
                                path="/quick_launch/:serviceType/:deploymentId"
                                element={<QuickLaunchPage />}
                              >
                                <Route
                                  path="help/:helpId"
                                  element={<QuickLaunchHelpPane />}
                                />
                              </Route>
                              <Route path="/" element={<Layout />}>
                                <Route index element={<WelcomePage />} />
                                <Route
                                  path="/dashboard/:deploymentId"
                                  element={<DashboardPage />}
                                />
                                <Route
                                  path="/my_environment"
                                  element={<MyEnvironmentPage />}
                                />
                                <Route
                                  path="/my_environment/:deploymentId"
                                  element={<MyEnvironmentPage />}
                                >
                                  <Route
                                    path="help/:helpId"
                                    element={<MyEnvironmentHelpPane />}
                                  />
                                </Route>
                                <Route
                                  path="/my_old_environment"
                                  element={<MyOldEnvironmentPage />}
                                />
                                <Route
                                  path="/my_old_environment/:deploymentId"
                                  element={<MyOldEnvironmentPage />}
                                >
                                  <Route
                                    path="help/:helpId"
                                    element={<MyOldEnvironmentHelpPane />}
                                  />
                                </Route>
                                <Route
                                  path="/service_statuses"
                                  element={<ServiceStatusesPage />}
                                />
                                <Route
                                  path="/service_statuses/:stackId"
                                  element={<ServiceStatusesPage />}
                                />
                                <Route path="/my_team" element={<MyTeamPage />}>
                                  <Route
                                    path=":userId"
                                    element={<EditUserPane />}
                                  />
                                </Route>
                                <Route
                                  path="/organizations"
                                  element={<OrganizationsPage />}
                                >
                                  <Route
                                    path=":organizationId"
                                    element={<EditOrganizationPane />}
                                  />
                                </Route>
                                <Route
                                  path="/profile"
                                  element={<ProfilePage />}
                                />
                                <Route
                                  path="/my_any_stack"
                                  element={<MyAnyStackPage />}
                                />
                              </Route>
                            </Routes>
                          </OnboarderContainer>
                        </JoyThemed>
                      </EnvironmentsProvider>
                    </FeaturesProvider>
                  </AlertsProvider>
                </BgwProvider>
              }
            />
          }
        />
      </Routes>
    </BrowserRouter>
  );
};

export default App;
